import { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Grid,
    Typography,
    Divider,
} from '@mui/material';
import {
    Twitter,
    Facebook,
    YouTube,
    Instagram,
} from '@mui/icons-material';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../firebase/config';
import { useFeedbackModal } from '../../contexts/FeedbackModalContext';
import FooterBottomBar from './footer/FooterBottomBar';
import FooterLinksSection from './footer/FooterLinksSection';

const quickLinks = [
    { label: 'Programs', path: '/programs' },
    { label: 'Club Events', path: '/events' },
    { label: 'Registration', path: '/registration' },
    { label: 'Calendar', path: '/calendar' },
    { label: 'About', path: '/about' },
    { label: 'Contact', path: '/contact' },
];

const programLinks = [
    { label: 'Intro', path: '/intro' },
    { label: 'Freeski', path: '/freeski' },
    { label: 'Mid-Week', path: '/mid-week' },
    { label: 'U12', path: '/u12' },
    { label: 'U14', path: '/u14' },
    { label: 'U16-U19', path: '/u16-u19' },
];

const resourceLinks = [
    { label: 'Scholarships', path: '/scholarships' },
    { label: 'Equipment', path: '/equipment-recommendations' },
    { label: 'Transportation', path: '/transportation' },
    { label: 'Lodging', path: '/lodging' },
    { label: 'SafeSport', path: '/safesport' },
];

const socialLinks = [
    {
        label: 'Twitter',
        icon: Twitter,
        url: 'https://twitter.com/tasskiteam',
    },
    {
        label: 'Facebook',
        icon: Facebook,
        url: 'https://www.facebook.com/TASskiRacing/',
    },
    {
        label: 'YouTube',
        icon: YouTube,
        url: 'https://www.youtube.com/channel/UCFkIaY_NvOIgYYrwAH2fHsA',
    },
    {
        label: 'Instagram',
        icon: Instagram,
        url: 'https://www.instagram.com/tasskiteam/',
    },
];

function Footer() {
    const [user, setUser] = useState(null);
    const { openFeedbackModal } = useFeedbackModal();

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
        });
        return () => unsubscribe();
    }, []);

    const filteredResourceLinks = resourceLinks.filter(link => {
        if (!user) {
            if (link.path === '/transportation' || 
                link.path === '/lodging' || 
                link.path === '/equipment-recommendations') {
                return false;
            }
        }
        return true;
    });

    return (
        <Box
            component="footer"
            sx={{
                bgcolor: 'mountain.main',
                color: 'white',
                pt: 8,
                pb: 4,
            }}
        >
            <Container maxWidth="lg">
                <Grid container spacing={4}>
                    {/* Logo and Description */}
                    <Grid
                        size={{
                            xs: 12,
                            md: 4
                        }}>
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 900,
                                letterSpacing: '0.05em',
                                mb: 2,
                            }}
                        >
                            TAS SKI TEAM
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ color: 'grey.400', mb: 2, lineHeight: 1.8 }}
                        >
                            Team Alpental Snoqualmie - Premier alpine ski racing program in the
                            Pacific Northwest since 1971. Making Future Olympians!
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'grey.400', mb: 1 }}>
                            <strong>Go Fast, Have Fun!</strong>
                        </Typography>
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="body2" sx={{ color: 'grey.400' }}>
                                Alpental-Snoqualmie Ski Foundation
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'grey.400' }}>
                                Tax ID: 23-7171420
                            </Typography>
                        </Box>
                    </Grid>

                    <FooterLinksSection
                        quickLinks={quickLinks}
                        programLinks={programLinks}
                        filteredResourceLinks={filteredResourceLinks}
                    />
                </Grid>

                <Divider sx={{ borderColor: 'grey.800', my: 4 }} />

                <FooterBottomBar
                    user={user}
                    openFeedbackModal={openFeedbackModal}
                    socialLinks={socialLinks}
                />

                {/* Partner logos placeholder */}
                <Box
                    sx={{
                        mt: 4,
                        pt: 4,
                        borderTop: '1px solid',
                        borderColor: 'grey.800',
                        textAlign: 'center',
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{ color: 'grey.400', display: 'block', mb: 2 }}
                    >
                        Partners in Winter Recreation
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'grey.400' }}>
                        This program provides employment, services, and privileges regardless of
                        race, color, creed, sex, religion, age or national origin.
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
}

export default Footer;
